import {
  Modal,
  Form,
  Descriptions,
  Divider,
  message,
} from '@pankod/refine-antd'
import React, { useCallback, useEffect, useState } from 'react'
import { useList, useUpdate } from '@pankod/refine-core'

import {
  CalonPesertaDidikResponse,
  DiklatProgramLocal,
} from '@resources/pendaftaran-diklat/types'
import TextFieldWithEmpty from '@resources/pendaftaran-diklat/block/TextFieldWithEmpty'
import TableSelectProgramLocal from '@resources/pendaftaran-diklat/penyetujuan-peserta-didik/block/TableSelectProgramLocal'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { PROGRAM_LOCAL_STATE } from '@resources/lms-management/program-lokal/types'

type UseUpdateProgramLocalModalState = CalonPesertaDidikResponse

export const useUpdateProgramLocalModal = () => {
  const [selectedPeserta, setSelectedPeserta] =
    useState<CalonPesertaDidikResponse | null>(null)

  const onClose = useCallback(() => {
    setSelectedPeserta(null)
  }, [])

  return {
    show: (peserta: UseUpdateProgramLocalModalState) => {
      setSelectedPeserta(peserta)
    },
    onClose,
    peserta: selectedPeserta,
  }
}

type ModalFormUpdateProgramLocal = {
  peserta: CalonPesertaDidikResponse | null
  id?: string
  onClose: () => void
  onSuccess?: () => void
}

const ModalFormUpdateProgramLocal = ({
  onClose,
  peserta,
  onSuccess,
}: ModalFormUpdateProgramLocal) => {
  const data: Partial<CalonPesertaDidikResponse> = {
    ...(peserta || {}),
  }
  const { data: programLocalList } = useList<DiklatProgramLocal>({
    resource: `diklat/programs/${data?.program?.id}/local-programs`,
    dataProviderName: 'lms',
    queryOptions: {
      enabled: !!peserta && !!data?.program?.id,
    },
    config: {
      filters: [
        {
          field: 'pageSize',
          value: -1,
          operator: 'eq',
        },
      ],
    },
  })
  const { mutateAsync: doUpdate } = useUpdate()
  const [form] = Form.useForm()
  const programLocalId = Form.useWatch(['programLocalId'], form)

  useEffect(() => {
    if (peserta) {
      form.setFieldsValue({
        programLocalId: peserta.programLocal?.id || null,
      })
    }
  }, [peserta, form])

  return (
    <Modal
      width="1000px"
      visible={!!peserta}
      getContainer={false}
      forceRender
      centered
      onCancel={onClose}
      title="Ubah Program Lokal"
      okText="Simpan"
      cancelText="Batal"
      maskClosable={false}
      afterClose={() => {
        form.resetFields()
      }}
      onOk={async () => {
        if (!peserta) return
        await form.validateFields()
        const formData = form.getFieldsValue()
        return await doUpdate(
          {
            id: peserta.id,
            resource: 'diklat/peserta-diklat',
            values: formData,
            dataProviderName: 'lms',
            successNotification: {
              message: 'Data program lokal berhasil diubah',
              type: 'success',
              description: 'Sukses',
            },
            errorNotification: (error) =>
              showErrorNotification(
                error,
                'Terdapat gangguan saat mengubah program lokal',
              ),
            metaData: {
              httpMethod: 'patch',
            },
          },
          {
            onSuccess: () => {
              onSuccess?.()
              onClose()
            },
          },
        )
      }}
      okButtonProps={{
        disabled: !programLocalId,
      }}
      destroyOnClose
    >
      <Descriptions
        title="Informasi Calon Peserta Didik"
        labelStyle={{ fontWeight: 'bold', width: '150px' }}
        bordered
        column={2}
        className="!mb-4"
        size="small"
      >
        <Descriptions.Item label="Nama Peserta">{data?.nama}</Descriptions.Item>
        <Descriptions.Item label="Provinsi">
          <TextFieldWithEmpty value={data?.provinsi} />
        </Descriptions.Item>
        <Descriptions.Item label="Email">{data?.email}</Descriptions.Item>
        <Descriptions.Item label="Kota/Kabupaten">
          <TextFieldWithEmpty value={data?.kotaKabupaten} />
        </Descriptions.Item>
        <Descriptions.Item label="NPSN">{data?.npsn}</Descriptions.Item>
        <Descriptions.Item label="Kecamatan">
          <TextFieldWithEmpty value={data?.kecamatan} />
        </Descriptions.Item>
        <Descriptions.Item label="Nama Sekolah" span={2}>
          {data?.namaSekolah}
        </Descriptions.Item>
        <Descriptions.Item label="Program" span={2}>
          {data?.program?.name}
        </Descriptions.Item>
        <Descriptions.Item label="Program Lokal" span={2}>
          <TextFieldWithEmpty
            value={data?.programLocal?.name}
            placeholder="Belum diatur"
          />
        </Descriptions.Item>
      </Descriptions>
      <Divider orientation="left" dashed className="!mb-0">
        Pilih Program Lokal
      </Divider>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...data,
        }}
        onFinishFailed={() => {
          message.error('Progam lokal harus dipilih!')
        }}
      >
        <Form.Item
          name="programLocalId"
          noStyle
          required
          rules={[{ required: true }]}
        >
          <TableSelectProgramLocal
            dataSource={programLocalList?.data || []}
            onRow={({ id, status }) => ({
              className:
                status === PROGRAM_LOCAL_STATE.DRAFT
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed',
              onClick: () => {
                if (status !== PROGRAM_LOCAL_STATE.DRAFT) return
                form.setFieldsValue({ programLocalId: id })
              },
              title:
                status !== PROGRAM_LOCAL_STATE.DRAFT
                  ? 'Status program lokal sudah bukan persiapan'
                  : undefined,
            })}
            rowSelection={{
              onChange: (rowKeys) => {
                form.setFieldsValue({
                  programLocalId: rowKeys[0],
                })
              },
              selectedRowKeys: [programLocalId],
              type: 'radio',
              getCheckboxProps(record) {
                return { disabled: record.status !== PROGRAM_LOCAL_STATE.DRAFT }
              },
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ModalFormUpdateProgramLocal
